import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import TransfersPanel from "@/components/TransfersPanel";
import ClientsPanel from "@/components/ClientsPanel";
import GroupagePanel from "@/components/GroupagePanel";
import ReportsPanel from "@/components/ReportsPanel";
import FinancePanel from "@/components/FinancePanel";
import CoifPanel from "@/components/CoifPanel";
import MessagesPanel from "@/components/MessagesPanel";
import { Plus, Trash2, ShieldCheck, Users, Boxes, MessageSquare, CheckCircle2, Inbox, Send, Bell, Check, Package, Truck, Pencil, X, Save, Upload, Image as ImageIcon, Copy, ArrowUp, ArrowDown, MapPin, MessageCircle, FileText } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-flat p-6">
      <Icon className="text-[var(--accent)]" />
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-4">{label}</p>
      <p className="font-display text-3xl font-black mt-1">{value}</p>
    </div>
  );
}

const EMPTY_PRODUCT = {
  name: "", description: "", price: 0, currency: "EUR", category: "general", stock: 0,
  image_url: "", images: [], in_stock_senegal: false,
  translations: {
    en: { name: "", description: "" },
    zh: { name: "", description: "" },
    ar: { name: "", description: "" },
    es: { name: "", description: "" },
  },
};

const PLACEHOLDER = {
  en: { name: "Name (English) — optional", description: "Description (English) — optional" },
  zh: { name: "名称 (中文) — 可选", description: "描述 (中文) — 可选" },
  ar: { name: "الاسم (العربية) — اختياري", description: "الوصف (العربية) — اختياري" },
  es: { name: "Nombre (Español) — opcional", description: "Descripción (Español) — opcional" },
};

const DAKAR_PHONE = "221774419060";
const DAKAR_ARRIVAL_STATUSES = new Set(["arrived_port", "customs_clearance", "out_for_delivery"]);

function dakarWaMessage(s, evStatus) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const trackUrl = `${base}/suivi?n=${s.tracking_number}`;
  const statusMap = {
    arrived_port: "vient d'ARRIVER à Dakar",
    customs_clearance: "est en DÉDOUANEMENT à Dakar",
    out_for_delivery: "est PRÊT À ÊTRE LIVRÉ",
  };
  const st = statusMap[evStatus] || "a un nouvel événement";
  const clientPhone = s.client_phone ? `\n📱 Tél client : ${s.client_phone}` : "";
  const amount = s.amount_due
    ? `\n💰 À encaisser : ${Math.round(s.amount_due).toLocaleString("fr-FR")} ${s.amount_currency || "FCFA"}`
    : "";
  return (
    `🚚 *Négosino — Arrivée Dakar*\n\n` +
    `Le colis ${s.tracking_number} ${st}.\n\n` +
    `👤 Client : ${s.client_name}${clientPhone}\n` +
    `📦 Contenu : ${(s.description || "").slice(0, 120)}${amount}\n\n` +
    `Merci de contacter le client pour organiser le retrait.\n\n` +
    `🔗 Suivi : ${trackUrl}`
  );
}

function buildDakarWaUrl(s, evStatus) {
  return `https://wa.me/${DAKAR_PHONE}?text=${encodeURIComponent(dakarWaMessage(s, evStatus))}`;
}

function buildWhatsAppUrl(phone, message) {
  const clean = String(phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
}

function waMessageFor(s) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const trackUrl = `${base}/suivi?n=${s.tracking_number}`;
  const svcMap = { express: "fret express aérien", normal: "fret aérien", maritime: "fret maritime" };
  const svc = svcMap[s.service_type] || s.service_type || "";
  const status = (s.current_status || "").replace(/_/g, " ");
  const amount = s.amount_due
    ? `\nMontant à payer à l'arrivée : ${Math.round(s.amount_due).toLocaleString("fr-FR")} ${s.amount_currency || "FCFA"}\n`
    : "\n";
  return (
    `Bonjour ${s.client_name || ""},\n\n` +
    `Votre expédition Negosino (${svc}) est enregistrée.\n` +
    `Numéro de suivi : ${s.tracking_number}\n` +
    `Statut actuel : ${status}\n` +
    `${s.origin} → ${s.destination}` +
    amount +
    `\nSuivez votre colis en direct : ${trackUrl}\n\n` +
    `— L'équipe Negosino`
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [shipments, setShipments] = useState([]);
  const [shipForm, setShipForm] = useState({
    client_name: "", client_email: "", client_phone: "", description: "",
    service_type: "normal", cargo_type: "standard",
    origin: "Yiwu, Chine", destination: "Dakar, Sénégal",
    weight_kg: "", volume_cbm: "", estimated_delivery: "",
    amount_due: "", amount_currency: "XOF", photos: [],
  });
  const [expandedShipment, setExpandedShipment] = useState(null);
  const [eventForm, setEventForm] = useState({ status: "picked_up", location: "", note: "" });
  const [activeSession, setActiveSession] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [tab, setTab] = useState("overview");
  const [langTab, setLangTab] = useState("fr");
  const [err, setErr] = useState("");
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const isMgr = user?.role === "manager";
      if (isMgr) {
        const [p, sh] = await Promise.all([
          api.get("/products"),
          api.get("/admin/shipments"),
        ]);
        setProducts(p.data); setShipments(sh.data);
        return;
      }
      const [s, p, m, ss, n, sh] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products"),
        api.get("/admin/contact"),
        api.get("/admin/support/sessions"),
        api.get("/admin/notifications"),
        api.get("/admin/shipments"),
      ]);
      setStats(s.data); setProducts(p.data); setMessages(m.data); setSessions(ss.data); setNotifs(n.data); setShipments(sh.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => {
    if (user?.role === "manager") return;
    const i = setInterval(async () => {
      try { const r = await api.get("/admin/notifications"); setNotifs(r.data); } catch {}
    }, 15000);
    return () => clearInterval(i);
  }, [user]);

  useEffect(() => {
    if (!activeSession) return;
    let stop = false;
    const fetchThread = async () => {
      const r = await api.get(`/support/messages/${activeSession}`);
      if (!stop) setThread(r.data);
    };
    fetchThread();
    const i = setInterval(fetchThread, 3500);
    return () => { stop = true; clearInterval(i); };
  }, [activeSession]);

  const submitProduct = async (e) => {
    e.preventDefault();
    try {
      const trs = form.translations || {};
      const cleanedTrs = {};
      for (const code of ["en", "zh", "ar", "es"]) {
        const tr = trs[code];
        if (tr && (tr.name?.trim() || tr.description?.trim())) {
          cleanedTrs[code] = { name: tr.name || "", description: tr.description || "" };
        }
      }
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock || 0),
        images: Array.isArray(form.images) ? form.images.filter(Boolean) : [],
        in_stock_senegal: !!form.in_stock_senegal,
        translations: Object.keys(cleanedTrs).length ? cleanedTrs : undefined,
      };
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      setForm(EMPTY_PRODUCT);
      setEditingId(null);
      setLangTab("fr");
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setLangTab("fr");
    setForm({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? 0,
      currency: p.currency || "EUR",
      category: p.category || "general",
      stock: p.stock ?? 0,
      image_url: p.image_url || "",
      images: Array.isArray(p.images) && p.images.length ? p.images : (p.image_url ? [p.image_url] : []),
      in_stock_senegal: !!p.in_stock_senegal,
      translations: {
        en: { name: p.translations?.en?.name || "", description: p.translations?.en?.description || "" },
        zh: { name: p.translations?.zh?.name || "", description: p.translations?.zh?.description || "" },
        ar: { name: p.translations?.ar?.name || "", description: p.translations?.ar?.description || "" },
        es: { name: p.translations?.es?.name || "", description: p.translations?.es?.description || "" },
      },
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_PRODUCT);
    setLangTab("fr");
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Fichier trop volumineux (max 8 Mo).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setForm((f) => {
        const imgs = Array.isArray(f.images) ? [...f.images] : [];
        if (!imgs.includes(fullUrl)) imgs.push(fullUrl);
        return { ...f, images: imgs, image_url: f.image_url || fullUrl };
      });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (url) => {
    setForm((f) => {
      const imgs = (f.images || []).filter((x) => x !== url);
      const image_url = f.image_url === url ? (imgs[0] || "") : f.image_url;
      return { ...f, images: imgs, image_url };
    });
  };

  const setPrimaryImage = (url) => {
    setForm((f) => {
      const imgs = (f.images || []).filter(Boolean);
      const rest = imgs.filter((x) => x !== url);
      return { ...f, image_url: url, images: [url, ...rest] };
    });
  };

  const addUrlToGallery = () => {
    const url = (form.image_url || "").trim();
    if (!url) return;
    setForm((f) => {
      const imgs = Array.isArray(f.images) ? [...f.images] : [];
      if (!imgs.includes(url)) imgs.push(url);
      return { ...f, images: imgs, image_url: f.image_url || url };
    });
  };

  const duplicateProduct = async (id) => {
    try {
      await api.post(`/admin/products/${id}/duplicate`);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const moveProduct = async (idx, direction) => {
    const next = idx + direction;
    if (next < 0 || next >= products.length) return;
    const list = [...products];
    const [item] = list.splice(idx, 1);
    list.splice(next, 0, item);
    setProducts(list); // optimistic
    try {
      await api.post("/admin/products/reorder", { ordered_ids: list.map((p) => p.id) });
    } catch (e) {
      setErr(formatApiError(e));
      load();
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${id}`);
    if (editingId === id) cancelEdit();
    load();
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeSession) return;
    await api.post("/admin/support/reply", { session_id: activeSession, message: reply });
    setReply("");
    const r = await api.get(`/support/messages/${activeSession}`);
    setThread(r.data);
    load();
  };

  const markNotif = async (id) => { await api.post(`/admin/notifications/${id}/read`); load(); };
  const markAll = async () => { await api.post("/admin/notifications/read-all"); load(); };

  const createShipment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...shipForm,
        weight_kg: shipForm.weight_kg ? parseFloat(shipForm.weight_kg) : null,
        volume_cbm: shipForm.volume_cbm ? parseFloat(shipForm.volume_cbm) : null,
        estimated_delivery: shipForm.estimated_delivery || null,
        amount_due: shipForm.amount_due ? parseFloat(shipForm.amount_due) : null,
        photos: Array.isArray(shipForm.photos) ? shipForm.photos.filter(Boolean) : [],
      };
      const { data } = await api.post("/admin/shipments", payload);
      setShipForm({ client_name: "", client_email: "", client_phone: "", description: "",
        service_type: "normal", cargo_type: "standard", origin: "Yiwu, Chine", destination: "Dakar, Sénégal",
        weight_kg: "", volume_cbm: "", estimated_delivery: "",
        amount_due: "", amount_currency: "XOF", photos: [] });
      alert(`Expédition créée. N° de suivi : ${data.tracking_number}`);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const RATE_MAP = { express: 12000, normal: 8000, maritime_standard: 170000, maritime_tiles: 200000 };
  const autoAmount = () => {
    const st = shipForm.service_type;
    let rate = 0;
    let amt = 0;
    if (st === "maritime") {
      rate = shipForm.cargo_type === "tiles" ? RATE_MAP.maritime_tiles : RATE_MAP.maritime_standard;
      amt = (parseFloat(shipForm.volume_cbm) || 0) * rate;
    } else {
      rate = RATE_MAP[st] || 0;
      amt = (parseFloat(shipForm.weight_kg) || 0) * rate;
    }
    if (amt > 0) setShipForm((f) => ({ ...f, amount_due: String(Math.round(amt)), amount_currency: "XOF" }));
  };

  const uploadShipmentPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Fichier trop volumineux (max 8 Mo)."); return; }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setShipForm((f) => ({ ...f, photos: [...(f.photos || []), fullUrl] }));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removeShipmentPhoto = (url) => {
    setShipForm((f) => ({ ...f, photos: (f.photos || []).filter((x) => x !== url) }));
  };

  const addEvent = async (shipmentId) => {
    if (!eventForm.status) return;
    const shipment = shipments.find((x) => x.id === shipmentId);
    const wasDakarArrival = DAKAR_ARRIVAL_STATUSES.has(eventForm.status);
    await api.post(`/admin/shipments/${shipmentId}/events`, eventForm);
    if (wasDakarArrival && shipment && window.confirm("Événement Dakar ajouté. Notifier l'agence Dakar (+221 77 441 90 60) sur WhatsApp ?")) {
      window.open(buildDakarWaUrl(shipment, eventForm.status), "_blank", "noopener");
    }
    setEventForm({ status: "picked_up", location: "", note: "" });
    load();
  };

  const deleteShipment = async (id) => {
    if (!window.confirm("Supprimer cette expédition ?")) return;
    await api.delete(`/admin/shipments/${id}`);
    load();
  };

  if (!user || user === false) return null;
  const isManager = user.role === "manager";
  const isAdmin = user.role === "admin";
  if (!isAdmin && !isManager) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="admin-denied">
      <h1 className="font-display text-3xl font-black">Accès refusé</h1>
      <p className="text-[var(--ink-soft)] mt-3">Vous devez être administrateur ou manager pour accéder à cette page.</p>
    </div>
  );

  // Manager (Anta) : accès limité au Groupage + vue lecture des Expéditions
  const MANAGER_TABS = new Set(["groupage", "shipments"]);
  const ALL_TABS = [
    { k: "overview", l: "Vue d'ensemble" },
    { k: "analytics", l: "Statistiques" },
    { k: "clients", l: "Clients" },
    { k: "products", l: "Produits" },
    { k: "shipments", l: isManager ? "Expéditions (lecture)" : "Expéditions" },
    { k: "groupage", l: "Groupage" },
    { k: "reports", l: "Rapports" },
    { k: "finance", l: "Finance 💰" },
    { k: "transfers", l: "Transferts $" },
    { k: "coif", l: "COIF 🎓" },
    { k: "messages", l: "Messages" },
    { k: "support", l: "Support live" },
    { k: "notifications", l: "Notifications", badge: notifs.unread },
  ];
  const TABS = isManager ? ALL_TABS.filter((t) => MANAGER_TABS.has(t.k)) : ALL_TABS;
  // Force manager on default allowed tab on first render
  if (isManager && !MANAGER_TABS.has(tab)) {
    setTimeout(() => setTab("groupage"), 0);
  }

  const setTr = (l, field, value) => setForm((f) => ({
    ...f,
    translations: { ...f.translations, [l]: { ...(f.translations?.[l] || {}), [field]: value } },
  }));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="kicker !text-[var(--secondary)] flex items-center gap-2"><ShieldCheck size={14} /> Administration</p>
          <h1 className="font-display text-4xl lg:text-5xl font-black mt-3">Tableau de bord</h1>
        </div>
        {notifs.unread > 0 && (
          <button onClick={() => setTab("notifications")} className="relative p-2 hover:bg-[var(--bg-soft)] rounded-full" data-testid="admin-bell">
            <Bell />
            <span className="absolute -top-1 -right-1 bg-[var(--accent)] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
              {notifs.unread}
            </span>
          </button>
        )}
      </div>
      {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm mt-4">{err}</div>}

      <div className="flex flex-wrap gap-2 border-b border-black/10 mt-8 mb-8">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 ${tab === t.k ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`}>
            {t.l}
            {t.badge > 0 && <span className="bg-[var(--accent)] text-white text-[10px] rounded-full px-1.5 py-0.5">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat icon={Users} label="Utilisateurs" value={stats.users} />
          <Stat icon={Boxes} label="Produits" value={stats.products} />
          <Stat icon={MessageSquare} label="Demandes contact" value={stats.contact} />
          <Stat icon={CheckCircle2} label="Paiements validés" value={stats.paid_transactions} />
        </div>
      )}

      {tab === "analytics" && <AnalyticsPanel />}
      {tab === "clients" && <ClientsPanel />}
      {tab === "groupage" && <GroupagePanel />}
      {tab === "reports" && <ReportsPanel />}
      {tab === "finance" && <FinancePanel />}
      {tab === "transfers" && <TransfersPanel />}
      {tab === "coif" && <CoifPanel />}

      {tab === "products" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form onSubmit={submitProduct} className="card-flat p-6 space-y-3 lg:col-span-1" data-testid="admin-product-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">
                {editingId ? "Modifier le produit" : "Ajouter un produit"}
              </h3>
              {editingId && (
                <button type="button" onClick={cancelEdit} className="text-xs text-[var(--ink-soft)] hover:text-black inline-flex items-center gap-1" data-testid="admin-product-cancel">
                  <X size={14} /> Annuler
                </button>
              )}
            </div>

            <div className="flex gap-1 border-b border-black/10 flex-wrap">
              {[
                { k: "fr", l: "🇫🇷 FR" },
                { k: "en", l: "🇬🇧 EN" },
                { k: "zh", l: "🇨🇳 中文" },
                { k: "ar", l: "🇸🇦 العربية" },
                { k: "es", l: "🇪🇸 ES" },
              ].map((l) => (
                <button
                  key={l.k}
                  type="button"
                  onClick={() => setLangTab(l.k)}
                  data-testid={`prod-lang-tab-${l.k}`}
                  className={`px-3 py-1.5 text-xs font-bold ${langTab === l.k ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`}
                >
                  {l.l}
                </button>
              ))}
            </div>

            {langTab === "fr" ? (
              <>
                <input required placeholder="Nom (français)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" />
                <textarea required placeholder="Description (français)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field min-h-[100px]" />
              </>
            ) : (
              <>
                <input
                  placeholder={PLACEHOLDER[langTab]?.name || ""}
                  value={form.translations?.[langTab]?.name || ""}
                  onChange={(e) => setTr(langTab, "name", e.target.value)}
                  className="input-field"
                  dir={langTab === "ar" ? "rtl" : "ltr"}
                  data-testid={`prod-name-${langTab}`}
                />
                <textarea
                  placeholder={PLACEHOLDER[langTab]?.description || ""}
                  value={form.translations?.[langTab]?.description || ""}
                  onChange={(e) => setTr(langTab, "description", e.target.value)}
                  className="input-field min-h-[100px]"
                  dir={langTab === "ar" ? "rtl" : "ltr"}
                  data-testid={`prod-desc-${langTab}`}
                />
              </>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-black/5">
              <input required type="number" step="0.01" placeholder="Prix" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-field" />
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input-field">
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="CNY">CNY</option>
                <option value="XOF">XOF (FCFA)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field" />
              <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-field" />
            </div>
            <div className="space-y-2 pt-2 border-t border-black/5">
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] flex items-center gap-1">
                <ImageIcon size={12} /> Photos du produit
                {form.images?.length > 0 && <span className="ml-1 text-[var(--accent)] normal-case tracking-normal">({form.images.length})</span>}
              </label>

              {form.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="admin-gallery">
                  {form.images.map((url, i) => (
                    <div key={url + i} className={`relative aspect-square rounded overflow-hidden bg-[var(--bg-soft)] group ${form.image_url === url ? "ring-2 ring-[var(--accent)]" : ""}`}>
                      <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                      {form.image_url === url && (
                        <span className="absolute top-1 left-1 text-[9px] font-bold uppercase tracking-widest bg-[var(--accent)] text-white px-1.5 py-0.5 rounded">Principale</span>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        {form.image_url !== url && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(url)}
                            className="bg-white text-black text-[10px] font-bold px-2 py-1 rounded"
                            title="Définir comme principale"
                            data-testid={`admin-set-primary-${i}`}
                          >
                            ★
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(url)}
                          className="bg-red-500 text-white rounded-full p-1"
                          title="Retirer"
                          data-testid={`admin-remove-image-${i}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <label className={`btn-outline !py-2 !px-3 text-xs w-full justify-center cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="admin-image-upload-label">
                <Upload size={14} /> {uploading ? "Envoi..." : "Ajouter une photo"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }}
                  className="hidden"
                  data-testid="admin-image-upload"
                  disabled={uploading}
                />
              </label>

              <div className="flex gap-2">
                <input
                  placeholder="ou coller une URL d'image"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="input-field text-xs flex-1"
                  data-testid="admin-image-url"
                />
                <button
                  type="button"
                  onClick={addUrlToGallery}
                  disabled={!form.image_url?.trim() || form.images?.includes(form.image_url?.trim())}
                  className="btn-outline !py-2 !px-3 text-xs disabled:opacity-40"
                  data-testid="admin-add-url"
                >
                  <Plus size={12} />
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 pt-2 border-t border-black/5 cursor-pointer" data-testid="admin-senegal-toggle-label">
              <input
                type="checkbox"
                checked={!!form.in_stock_senegal}
                onChange={(e) => setForm({ ...form, in_stock_senegal: e.target.checked })}
                className="w-4 h-4 accent-[var(--accent)]"
                data-testid="admin-senegal-toggle"
              />
              <span className="text-sm inline-flex items-center gap-1"><MapPin size={14} className="text-[var(--accent)]" /> En stock au Sénégal (livraison rapide)</span>
            </label>
            <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-product-submit">
              {editingId ? (<><Save size={16} /> Enregistrer</>) : (<><Plus size={16} /> Créer</>)}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-3">
            {products.map((p, idx) => (
              <div key={p.id} className={`card-flat p-4 flex items-center gap-3 ${editingId === p.id ? "ring-2 ring-[var(--accent)]" : ""}`} data-testid={`admin-product-${p.id}`}>
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveProduct(idx, -1)}
                    disabled={idx === 0}
                    className="text-[var(--ink-soft)] hover:text-[var(--accent)] disabled:opacity-20 disabled:cursor-not-allowed p-1"
                    title="Monter"
                    data-testid={`up-product-${p.id}`}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => moveProduct(idx, 1)}
                    disabled={idx === products.length - 1}
                    className="text-[var(--ink-soft)] hover:text-[var(--accent)] disabled:opacity-20 disabled:cursor-not-allowed p-1"
                    title="Descendre"
                    data-testid={`down-product-${p.id}`}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
                {p.image_url && <img src={p.image_url} alt="" className="w-16 h-16 rounded object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold truncate">{p.name}</p>
                    {p.in_stock_senegal && (
                      <span className="text-[9px] font-bold uppercase tracking-widest bg-[var(--accent)] text-white px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                        <MapPin size={9} /> SN
                      </span>
                    )}
                    {p.images?.length > 1 && (
                      <span className="text-[9px] font-bold uppercase tracking-widest bg-black/70 text-white px-1.5 py-0.5 rounded-full">
                        {p.images.length} photos
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--ink-soft)] mt-1">
                    {p.category} · {p.price} {p.currency} · stock {p.stock}
                    {p.translations && Object.keys(p.translations).length > 0 && (
                      <span className="ml-2 text-[var(--accent)]">🌐 {Object.keys(p.translations).join(", ")}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => duplicateProduct(p.id)} className="text-[var(--secondary)] p-2 hover:bg-[var(--secondary)]/10 rounded" title="Dupliquer" data-testid={`dup-product-${p.id}`}><Copy size={16} /></button>
                <button onClick={() => startEdit(p)} className="text-[var(--accent)] p-2 hover:bg-[var(--accent)]/10 rounded" title="Modifier" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                <button onClick={() => removeProduct(p.id)} className="text-red-500 p-2 hover:bg-red-50 rounded" title="Supprimer" data-testid={`del-product-${p.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "shipments" && (
        <div className={`grid grid-cols-1 gap-6 ${isManager ? "" : "lg:grid-cols-12"}`} data-testid="admin-shipments">
          {!isManager && (
          <form onSubmit={createShipment} className="card-flat p-6 space-y-3 lg:col-span-4" data-testid="admin-shipment-form">
            <h3 className="font-display text-xl font-bold flex items-center gap-2"><Package size={18} /> Nouvelle expédition</h3>
            <input required placeholder="Nom du client" value={shipForm.client_name} onChange={(e) => setShipForm({ ...shipForm, client_name: e.target.value })} className="input-field" />
            <input type="email" placeholder="Email client (optionnel)" value={shipForm.client_email} onChange={(e) => setShipForm({ ...shipForm, client_email: e.target.value })} className="input-field" />
            <input placeholder="Téléphone (optionnel)" value={shipForm.client_phone} onChange={(e) => setShipForm({ ...shipForm, client_phone: e.target.value })} className="input-field" />
            <textarea required placeholder="Description du contenu" value={shipForm.description} onChange={(e) => setShipForm({ ...shipForm, description: e.target.value })} className="input-field min-h-[80px]" />
            <select value={shipForm.service_type} onChange={(e) => setShipForm({ ...shipForm, service_type: e.target.value })} className="input-field">
              <option value="express">Fret express — Aérien (12 000 FCFA/kg)</option>
              <option value="normal">Fret normal — Aérien (8 000 FCFA/kg)</option>
              <option value="maritime">Fret maritime — Container</option>
            </select>
            {shipForm.service_type === "maritime" && (
              <div className="pt-1 space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Type de marchandise (tarif maritime)</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShipForm({ ...shipForm, cargo_type: "standard" })}
                    className={`p-3 rounded border-2 text-xs font-bold text-left transition-colors ${shipForm.cargo_type === "standard" ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-black/10 hover:border-black/20"}`}
                    data-testid="cargo-standard"
                  >
                    📦 Produits normaux<br />
                    <span className="text-[var(--accent)] text-sm">170 000 FCFA/m³</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShipForm({ ...shipForm, cargo_type: "tiles" })}
                    className={`p-3 rounded border-2 text-xs font-bold text-left transition-colors ${shipForm.cargo_type === "tiles" ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-black/10 hover:border-black/20"}`}
                    data-testid="cargo-tiles"
                  >
                    🧱 Carreaux<br />
                    <span className="text-[var(--accent)] text-sm">200 000 FCFA/m³</span>
                  </button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Origine" value={shipForm.origin} onChange={(e) => setShipForm({ ...shipForm, origin: e.target.value })} className="input-field" />
              <input placeholder="Destination" value={shipForm.destination} onChange={(e) => setShipForm({ ...shipForm, destination: e.target.value })} className="input-field" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" step="0.1" placeholder="Poids (kg)" value={shipForm.weight_kg} onChange={(e) => setShipForm({ ...shipForm, weight_kg: e.target.value })} className="input-field" />
              <input type="number" step="0.01" placeholder="Volume (m³)" value={shipForm.volume_cbm} onChange={(e) => setShipForm({ ...shipForm, volume_cbm: e.target.value })} className="input-field" />
            </div>
            <input type="date" placeholder="Livraison estimée" value={shipForm.estimated_delivery} onChange={(e) => setShipForm({ ...shipForm, estimated_delivery: e.target.value })} className="input-field" />

            <div className="pt-2 border-t border-black/5 space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">💰 Montant à payer à l'arrivée</label>
              <div className="flex gap-2">
                <input type="number" step="100" placeholder="Montant" value={shipForm.amount_due} onChange={(e) => setShipForm({ ...shipForm, amount_due: e.target.value })} className="input-field flex-1" data-testid="admin-shipment-amount" />
                <select value={shipForm.amount_currency} onChange={(e) => setShipForm({ ...shipForm, amount_currency: e.target.value })} className="input-field w-24">
                  <option value="XOF">FCFA</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="CNY">CNY</option>
                </select>
              </div>
              <button type="button" onClick={autoAmount} className="text-xs text-[var(--accent)] font-bold underline" data-testid="admin-shipment-auto-amount">
                ⚡ Calculer automatiquement (
                {shipForm.service_type === "express" && "12 000 × poids"}
                {shipForm.service_type === "normal" && "8 000 × poids"}
                {shipForm.service_type === "maritime" && shipForm.cargo_type === "tiles" && "200 000 × m³"}
                {shipForm.service_type === "maritime" && shipForm.cargo_type !== "tiles" && "170 000 × m³"}
                )
              </button>
            </div>

            <div className="pt-2 border-t border-black/5 space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] flex items-center gap-1">
                <ImageIcon size={12} /> Photos du produit ({(shipForm.photos || []).length})
              </label>
              {(shipForm.photos || []).length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {shipForm.photos.map((url, i) => (
                    <div key={url + i} className="relative aspect-square rounded overflow-hidden bg-[var(--bg-soft)] group">
                      <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeShipmentPhoto(url)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Retirer">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className={`btn-outline !py-2 !px-3 text-xs w-full justify-center cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="admin-shipment-photo-upload-label">
                <Upload size={14} /> {uploading ? "Envoi..." : "Ajouter une photo produit"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadShipmentPhoto(f); e.target.value = ""; }}
                  className="hidden"
                  data-testid="admin-shipment-photo-upload"
                  disabled={uploading}
                />
              </label>
            </div>

            <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-shipment-submit">
              <Plus size={16} /> Créer expédition
            </button>
          </form>
          )}

          <div className={isManager ? "space-y-3" : "lg:col-span-8 space-y-3"}>
            {shipments.length === 0 && <p className="text-[var(--ink-soft)] text-center py-8">Aucune expédition pour le moment.</p>}
            {shipments.map((s) => (
              <div key={s.id} className="card-flat p-5" data-testid={`shipment-${s.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-mono font-bold text-lg">{s.tracking_number}</p>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-[var(--accent)]/10 text-[var(--accent)]">
                        {s.service_type}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-[var(--secondary)]/10 text-[var(--secondary)]">
                        {s.current_status}
                      </span>
                    </div>
                    <p className="text-sm mt-2">{s.client_name} — {s.description}</p>
                    <p className="text-xs text-[var(--ink-soft)] mt-1">{s.origin} → {s.destination}</p>
                    {(s.client_phone || s.client_email) && (
                      <p className="text-[10px] text-[var(--ink-soft)] mt-1">
                        {s.client_phone && <span>📱 {s.client_phone}</span>}
                        {s.client_phone && s.client_email && <span> · </span>}
                        {s.client_email && <span>✉️ {s.client_email}</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {s.client_phone && (
                      <a
                        href={buildWhatsAppUrl(s.client_phone, waMessageFor(s))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#25D366] hover:bg-[#1ea855] text-white !py-1.5 !px-3 text-xs rounded inline-flex items-center gap-1.5 font-bold"
                        title="Notifier le client sur WhatsApp"
                        data-testid={`wa-shipment-${s.id}`}
                      >
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                    <a
                      href={buildDakarWaUrl(s, s.current_status || "arrived_port")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`bg-[#00A86B] hover:bg-[#00834f] text-white !py-1.5 !px-3 text-xs rounded inline-flex items-center gap-1.5 font-bold ${isManager ? "hidden" : ""}`}
                      title="Notifier l'agence Dakar sur WhatsApp"
                      data-testid={`dakar-wa-shipment-${s.id}`}
                    >
                      🇸🇳 Dakar
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/shipments/${s.tracking_number}/receipt.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline !py-1.5 !px-3 text-xs"
                      title="Bon d'expédition PDF avec QR code"
                      data-testid={`pdf-shipment-${s.id}`}
                    >
                      <FileText size={13} /> PDF
                    </a>
                    <button onClick={() => setExpandedShipment(expandedShipment === s.id ? null : s.id)} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={`toggle-shipment-${s.id}`}>
                      <Truck size={14} /> {isManager ? "Historique" : "Événements"}
                    </button>
                    {!isManager && (
                      <button onClick={() => deleteShipment(s.id)} className="text-red-500 p-2" data-testid={`del-shipment-${s.id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>

                {expandedShipment === s.id && (
                  <div className="mt-5 pt-5 border-t border-black/5">
                    {!isManager && (
                    <>
                    <p className="kicker mb-3">Ajouter un événement</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <select value={eventForm.status} onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })} className="input-field">
                        <option value="picked_up">Récupéré à Yiwu</option>
                        <option value="in_transit_china">En transit en Chine</option>
                        <option value="in_transit">Transit international</option>
                        <option value="arrived_port">Arrivé port/aéroport</option>
                        <option value="customs_clearance">Dédouanement</option>
                        <option value="out_for_delivery">En livraison</option>
                        <option value="delivered">Livré</option>
                        <option value="exception">Incident</option>
                      </select>
                      <input placeholder="Lieu" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className="input-field" />
                      <input placeholder="Note" value={eventForm.note} onChange={(e) => setEventForm({ ...eventForm, note: e.target.value })} className="input-field" />
                      <button onClick={() => addEvent(s.id)} className="btn-primary justify-center" data-testid={`add-event-${s.id}`}>
                        <Plus size={14} /> Ajouter
                      </button>
                    </div>
                    </>
                    )}
                    <ul className="mt-5 space-y-2">
                      {[...(s.events || [])].reverse().map((ev) => (
                        <li key={ev.id} className="text-sm p-2 bg-[var(--bg-soft)] rounded flex justify-between gap-3">
                          <span className="font-semibold">{ev.status}</span>
                          <span className="text-[var(--ink-soft)]">{ev.location || "—"}</span>
                          <span className="text-xs text-[var(--ink-soft)]">{new Date(ev.at).toLocaleString("fr-FR")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "messages" && (
        <MessagesPanel messages={messages} onReload={load} />
      )}

      {tab === "support" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="admin-support">
          <div className="lg:col-span-4 space-y-2">
            <h3 className="font-display text-lg font-bold mb-2">Conversations</h3>
            {sessions.length === 0 && <p className="text-sm text-[var(--ink-soft)]">Aucune conversation.</p>}
            {sessions.map((s) => (
              <button key={s.session_id} onClick={() => setActiveSession(s.session_id)} data-testid={`session-${s.session_id}`}
                className={`w-full text-left card-flat p-4 ${activeSession === s.session_id ? "ring-2 ring-[var(--accent)]" : ""}`}>
                <p className="font-bold text-sm">{s.name || "Visiteur"}</p>
                <p className="text-xs text-[var(--ink-soft)] truncate">{s.last_message}</p>
              </button>
            ))}
          </div>
          <div className="lg:col-span-8 card-flat p-6 flex flex-col" style={{ minHeight: 400 }}>
            {!activeSession ? <p className="text-[var(--ink-soft)] m-auto">Sélectionnez une conversation</p> : (
              <>
                <div className="flex-1 overflow-y-auto flex flex-col gap-2 mb-4 max-h-[400px]">
                  {thread.map((t) => (
                    <div key={t.id} className={t.from_role === "admin" ? "chat-bubble-user" : "chat-bubble-bot"}>{t.message}</div>
                  ))}
                </div>
                <div className="flex gap-2 border-t border-black/10 pt-3">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Votre réponse…" className="input-field" data-testid="support-reply-input" />
                  <button onClick={sendReply} className="btn-primary !py-2 !px-3" data-testid="support-reply-send"><Send size={16} /></button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div data-testid="admin-notifications">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[var(--ink-soft)]">
              {notifs.unread} non lue{notifs.unread > 1 ? "s" : ""} sur {notifs.items.length}
            </p>
            {notifs.unread > 0 && (
              <button onClick={markAll} className="btn-outline !py-1.5 !px-3 text-xs" data-testid="notif-mark-all">
                <Check size={14} /> Tout marquer comme lu
              </button>
            )}
          </div>
          {notifs.items.length === 0 && (
            <div className="card-flat p-10 text-center text-[var(--ink-soft)]">
              <Bell className="mx-auto mb-3" />
              Aucune notification pour le moment.
            </div>
          )}
          <div className="space-y-2">
            {notifs.items.map((n) => (
              <div
                key={n.id}
                className={`card-flat p-4 flex gap-4 items-start ${n.read ? "opacity-60" : "border-l-4 border-l-[var(--accent)]"}`}
                data-testid={`notif-${n.id}`}
              >
                <div className={`w-2 h-2 mt-2 rounded-full ${n.read ? "bg-black/20" : "bg-[var(--accent)]"}`} />
                <div className="flex-1">
                  <div className="flex justify-between items-center gap-4">
                    <p className="font-bold">{n.title}</p>
                    <p className="text-xs text-[var(--ink-soft)]">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <p className="text-sm text-[var(--ink-soft)] mt-1 whitespace-pre-wrap">{n.body}</p>
                </div>
                {!n.read && (
                  <button onClick={() => markNotif(n.id)} className="text-xs text-[var(--accent)] font-bold" data-testid={`notif-read-${n.id}`}>
                    Marquer lu
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
